// https://mui.com/x/react-data-grid/state/#save-and-restore-the-state-from-external-storage
import React, { useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import { DataGridPremium, GridPagination, useGridApiRef } from '@mui/x-data-grid-premium';
import Box from '@mui/material/Box';
import NoAvailableCard from 'common/NoAvailableCard';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { formatNumber } from 'utils/helpers/format';
import DataProcessingCard from './DataProcessingCard';
import { AuthContext } from '../authentication/AuthContext';
import GridToolbar from './grid-toolbar/GridToolbar';

// Utility functions for handling nullable numeric values in aggregations
const normalizeVolumeValue = value => {
  if (value === null || value === undefined || value === '') return null;

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const formatGridNumber = ({ value }) => {
  if (value === null || value === undefined || value === '') return '';
  return formatNumber(value, { defaultValue: 0 });
};

// Override default aggregation functions to handle null values properly
export const defaultAggregationFunctions = {
  avg: {
    apply: ({ values = [] }) => {
      const numericValues = values
        .map(normalizeVolumeValue)
        .filter(value => typeof value === 'number' && !Number.isNaN(value));

      if (!numericValues.length) return null;

      const sum = numericValues.reduce((total, currentValue) => total + currentValue, 0);
      return sum / numericValues.length;
    },
    label: 'Average',
    valueFormatter: formatGridNumber,
  },
  sum: {
    apply: ({ values = [] }) => {
      const numericValues = values
        .map(normalizeVolumeValue)
        .filter(value => typeof value === 'number' && !Number.isNaN(value));

      if (!numericValues.length) return null;

      return numericValues.reduce((total, currentValue) => total + currentValue, 0);
    },
    label: 'Sum',
    valueFormatter: formatGridNumber,
  },
};

const PersistentDataGrid = props => {
  const { userProfile } = useContext(AuthContext);
  const { id, onServerSideStateRestore, persist } = props;
  const localApiRef = useGridApiRef();
  const apiRef = props.apiRef || localApiRef;
  const [initialState, setInitialState] = useState({});
  const [gridKey, setGridKey] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const hasLoadedInitialState = useRef(false);
  const saveTimeoutRef = useRef(null);
  const isMountedRef = useRef(false);

  // Process columns: apply persisted order/widths and restrict aggregation functions
  const processedColumns = React.useMemo(() => {
    if (!props.columns) return props.columns;

    const persistedDimensions = initialState?.columns?.dimensions;
    const persistedOrder = initialState?.columns?.orderedFields;

    // Reorder columns based on persisted orderedFields
    let cols = props.columns;
    if (persistedOrder?.length) {
      const colsByField = new Map(props.columns.map(c => [c.field, c]));
      const ordered = persistedOrder
        .filter(field => colsByField.has(field))
        .map(field => colsByField.get(field));
      const remaining = props.columns.filter(c => !persistedOrder.includes(c.field));
      cols = [...ordered, ...remaining];
    }

    return cols.map(col => {
      let result = col;

      // If this column has a persisted width, remove flex so the grid uses the saved width
      if (persistedDimensions?.[col.field]?.width && col.flex != null) {
        const { flex, ...rest } = result;
        result = rest;
      }

      const isNumericColumn = result.type === 'number';

      // If column has explicitly defined aggregation functions, don't modify
      if (result.availableAggregationFunctions) {
        return result;
      }

      // If it's a numeric column and aggregation isn't explicitly disabled
      if (isNumericColumn && result.aggregable !== false) {
        return {
          ...result,
          availableAggregationFunctions: ['sum', 'avg'],
        };
      }

      // For non-numeric columns, disable aggregation unless explicitly enabled
      if (!isNumericColumn && result.aggregable === undefined) {
        return {
          ...result,
          availableAggregationFunctions: [], // This will effectively disable aggregation for this column
        };
      }

      return result;
    });
  }, [props.columns, initialState]);

  const saveSnapshot = useCallback(() => {
    // Don't save if we haven't finished initial load - prevents overwriting with defaults
    if (!hasLoadedInitialState.current || !isMountedRef.current) return;

    if (apiRef?.current?.exportState && localStorage) {
      const currentState = apiRef.current.exportState();
      // Combine grid state with server-side state (for server-side grids)
      const stateToSave = {
        ...currentState,
        ...(props.serverSideState && { serverSide: props.serverSideState }),
      };
      localStorage.setItem(
        `${userProfile?.sub}.dataGridState.${props.id}`,
        JSON.stringify(stateToSave)
      );
    }
  }, [apiRef, props.id, props.serverSideState, userProfile]);

  // Debounced save to avoid excessive localStorage writes
  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveSnapshot();
    }, 500); // Save 500ms after last state change
  }, [saveSnapshot]);

  const resetSnapShot = () => {
    localStorage?.removeItem(`${userProfile?.sub}.dataGridState.${id}`);
    setInitialState({});
    setGridKey(state => state + 1);
    hasLoadedInitialState.current = false;
    // Call optional onReset callback for custom reset logic
    if (props.onReset) {
      props.onReset();
    }
  };

  const fullScreen = () => {
    setIsFullScreen(!isFullScreen);
    if (!isFullScreen) {
      apiRef.current.setPinnedColumns({ left: [], right: [] });
    }
  };

  useLayoutEffect(() => {
    if (persist === false) {
      hasLoadedInitialState.current = true; // Mark as loaded even when persistence is disabled
      return () => {};
    }

    // Only load from localStorage if we have a valid userProfile and haven't loaded yet
    if (userProfile?.sub && !hasLoadedInitialState.current) {
      const stateFromLocalStorage = localStorage?.getItem(`${userProfile.sub}.dataGridState.${id}`);
      if (stateFromLocalStorage) {
        const parsedState = JSON.parse(stateFromLocalStorage);
        // Extract server-side state if present and notify parent
        if (parsedState.serverSide && onServerSideStateRestore) {
          onServerSideStateRestore(parsedState.serverSide);
          // Remove serverSide from the state passed to grid
          const { serverSide, ...gridState } = parsedState;
          setInitialState(gridState);
        } else {
          setInitialState(parsedState);
        }
        // Force grid to remount with the loaded state
        setGridKey(state => state + 1);
      }
      hasLoadedInitialState.current = true;
    }

    // Set mounted flag after a short delay to ensure grid is initialized
    const mountTimer = setTimeout(() => {
      isMountedRef.current = true;
    }, 100);

    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      clearTimeout(mountTimer);
      isMountedRef.current = false;
      window.removeEventListener('beforeunload', saveSnapshot);
      // Clear any pending debounced save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Do a final save on unmount (actual unmount, not Strict Mode cleanup)
      saveSnapshot();
    };
  }, [id, saveSnapshot, userProfile?.sub, persist, onServerSideStateRestore]);

  const Toolbar = () => (
    <GridToolbar
      disabledExport={props.disabledExport}
      exportOptions={props.exportOptions}
      hideExportButton={props.hideExportButton}
      isExporting={props.isExporting}
      isFullScreen={isFullScreen}
      onExportClick={props.onExportClick}
      onFullScreenToggle={fullScreen}
      onResetView={resetSnapShot}
      quickFilter={props.quickFilter}
      showFilterButton={props.showFilterButton}
      showFullScreenButton={props.showFullScreenButton}
    />
  );

  const CustomPagination = () => (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: 'surface.light',
        display: 'flex',
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        margin: '0 auto',
        ...(isFullScreen && { maxWidth: '100vw' }),
        px: 3,
        width: '100%',
      }}
    >
      <Box sx={{ whiteSpace: 'nowrap' }}>{props.paginationStatus}</Box>
      <GridPagination sx={{ flexWrap: 'nowrap', width: 'auto' }} />
    </Box>
  );

  return (
    <Box
      sx={{
        '& .MuiDataGrid-main': {
          margin: '0 auto',
          ...(isFullScreen && { maxWidth: '100vw' }),
          width: '100%',
        },
        height: '100%',
        margin: '0 auto',
        width: '100%',
        ...(isFullScreen && {
          backgroundColor: 'surface.white',
          bottom: 0,
          height: `calc(100vh - 48px)`,
          left: 0,
          position: 'fixed',
          right: 0,
          zIndex: 1400,
        }),
      }}
    >
      <DataGridPremium
        {...props}
        aggregationFunctions={{
          ...defaultAggregationFunctions,
          ...props.aggregationFunctions,
        }}
        apiRef={apiRef}
        columns={processedColumns}
        initialState={{
          ...props.initialState,
          ...initialState,
          pinnedColumns: {
            left:
              initialState?.pinnedColumns?.left ?? props.initialState?.pinnedColumns?.left ?? [],
            right:
              initialState?.pinnedColumns?.right ?? props.initialState?.pinnedColumns?.right ?? [],
          },
        }}
        key={gridKey}
        onStateChange={persist ? debouncedSave : undefined}
        rowSelection={props.checkboxSelection || props.rowSelection || false}
        slotProps={{
          ...props.slotProps,
          filterPanel: { sx: { maxWidth: '100vw' } },
        }}
        slots={{
          loadingOverlay: () => (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <DataProcessingCard
                variant={
                  props.isExporting ? 'export' : props.isProcessing ? 'processing' : 'loading'
                }
              />
            </Box>
          ),
          noRowsOverlay: () => (
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                height: '100%',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              <NoAvailableCard />
            </Box>
          ),
          ...props.slots,
          footer: () => {
            const DeveloperFooter = props.slots?.footer;
            if (DeveloperFooter) {
              return <DeveloperFooter {...props.slotProps?.footer} />;
            }
            if (props.pagination === false) return null;
            return (
              <Box>
                <CustomPagination />
                {props.bulkActionFooter && <props.bulkActionFooter />}
              </Box>
            );
          },
          toolbar: () => <Toolbar />,
        }}
        sx={{
          // Ensure aggregation labels display properly
          '& .MuiDataGrid-aggregationColumnHeaderLabel': {
            display: 'block',
            fontSize: '0.75rem',
            marginTop: '2px',
          },

          // Increase column header height to accommodate aggregation labels
          '& .MuiDataGrid-columnHeader': {
            maxHeight: '56px !important',
            minHeight: '56px !important',
          },

          '& .MuiDataGrid-columnHeaderTitle': {
            lineHeight: '1.2',
            overflow: 'visible',
            whiteSpace: 'normal',
          },

          '& .MuiDataGrid-toolbarContainer': {
            margin: '0 auto',
            ...(isFullScreen && { maxWidth: '100vw' }),
            width: '100%',
          },
          ...(isFullScreen && {
            '& .MuiDataGrid-columnHeaders': {
              borderRadius: 0,
            },
            '& .MuiDataGrid-main': {
              px: 0,
            },
          }),
        }}
      />
    </Box>
  );
};

PersistentDataGrid.propTypes = {
  aggregationFunctions: shape({}),
  apiRef: shape({}),
  bulkActionFooter: func,
  checkboxSelection: bool,
  columns: arrayOf(shape({})).isRequired,
  disabledExport: bool,
  exportOptions: shape({}),
  hideExportButton: bool,
  id: string.isRequired,
  initialState: shape({}),
  isExporting: bool,
  isProcessing: bool,
  onExportClick: func,
  onReset: func,
  onSelectionReset: func,
  onServerSideStateRestore: func,
  pagination: bool,
  paginationStatus: string,
  persist: bool,
  quickFilter: bool,
  rowSelection: bool,
  serverSideState: shape({}),
  showFilterButton: bool,
  showFullScreenButton: bool,
  slotProps: shape({}),
  slots: shape({}),
};

PersistentDataGrid.defaultProps = {
  aggregationFunctions: {},
  apiRef: null,
  bulkActionFooter: null,
  checkboxSelection: false,
  disabledExport: false,
  exportOptions: null,
  hideExportButton: false,
  initialState: {},
  isExporting: false,
  isProcessing: false,
  onExportClick: null,
  onReset: null,
  onSelectionReset: null,
  onServerSideStateRestore: null,
  pagination: true,
  paginationStatus: '',
  persist: true,
  quickFilter: false,
  rowSelection: false,
  serverSideState: null,
  showFilterButton: true,
  showFullScreenButton: false,
  slotProps: null,
  slots: null,
};

export default PersistentDataGrid;
